/**
 * Deterministic pedestrian models.
 *
 * Research level: scripted trajectory (straight walk, then a subtle lateral
 * drift after cue onset) that ignores the robot during the research window,
 * followed by a deterministic reactive phase that resolves the encounter
 * naturally. All randomness comes from the seeded RNG (gait-sway phase only).
 */

import { Rng, deriveSeed } from "../core/rng";
import { clamp, smoothstep01 } from "../core/vec";
import {
  DRIFT_RAMP_S,
  DRIFT_TARGET,
  PED_MAX_X,
  PED_RADIUS,
  PED_REACTIVE_SLOW_SPEED,
  PED_REACTIVE_TTC,
  PED_SPAWN_Z,
  PED_WALK_SPEED,
  REACTIVE_CLEARANCE,
  SAFETY_MARGIN,
  SWAY_AMPLITUDE,
  SWAY_HZ,
} from "./constants";
import { timeToCollision } from "./safety";
import type { RobotShape } from "./world";
import type { PedestrianState, RobotState, Side, Variant } from "./types";

/** Max lateral adjustment speed (m/s) for both drift and reactive motion. */
const LATERAL_SPEED = 0.6;

export interface PedStepCtx {
  t: number;
  dt: number;
  /** Time of cue onset (s), or null before it. */
  cueT: number | null;
  /** True once the research window has ended and the pedestrian may react. */
  reactive: boolean;
  robot: Readonly<RobotState>;
  /** Robot collision footprint (two-circle model). */
  robotShape: RobotShape;
}

export interface PedestrianModel {
  readonly state: PedestrianState;
  step(ctx: PedStepCtx): void;
  /** The side (screen space) of the scripted drift, once discernible; null for variant C. */
  driftSide(): Side | null;
}

export class ResearchPedestrian implements PedestrianModel {
  readonly state: PedestrianState;
  private readonly swayPhase: number;
  private readonly driftTargetX: number;
  private readonly mirrorSign: 1 | -1;
  /** Sway-free lateral position: chases the base target only (safety TTC). */
  private baseX = 0;

  constructor(variant: Variant, mirrorSign: 1 | -1, seed: number) {
    const rng = new Rng(deriveSeed(seed, 1));
    this.swayPhase = rng.next() * Math.PI * 2;
    this.mirrorSign = mirrorSign;
    const logical = variant === "A" ? -DRIFT_TARGET : variant === "B" ? DRIFT_TARGET : 0;
    this.driftTargetX = logical * mirrorSign;
    this.state = {
      x: 0,
      z: PED_SPAWN_Z,
      vx: 0,
      vz: -PED_WALK_SPEED,
      safetyVx: 0,
      safetyVz: -PED_WALK_SPEED,
      active: true,
    };
  }

  driftSide(): Side | null {
    if (this.driftTargetX === 0) return null;
    return this.driftTargetX < 0 ? "left" : "right";
  }

  step(ctx: PedStepCtx): void {
    const s = this.state;
    if (!s.active) return;

    const prevX = s.x;
    const prevZ = s.z;
    const passed = s.z < ctx.robot.z - 1.0;

    // --- Lateral target. ---
    let baseTarget: number;
    let standing = false;
    if (ctx.reactive && !passed) {
      // Keep to whichever side of the robot the pedestrian is already on,
      // at a physical clearance. Continuous and mirror-symmetric.
      const side = s.x >= ctx.robot.x ? 1 : -1;
      baseTarget = clamp(
        ctx.robot.x + side * REACTIVE_CLEARANCE,
        -PED_MAX_X,
        PED_MAX_X,
      );
      // If the wall clamp leaves less than the side-by-side clearance
      // (robot half-width + ped radius + margin), the pass is physically
      // impossible right now: stand and wait instead of walking into the
      // (possibly safety-stopped) robot. Deterministic, mirror-symmetric,
      // and escapable — once the robot steers away, walking resumes.
      const lateralNeed = ctx.robotShape.r + PED_RADIUS + SAFETY_MARGIN;
      if (Math.abs(baseTarget - ctx.robot.x) < lateralNeed) {
        standing = true;
      }
    } else if (ctx.cueT !== null) {
      // Scripted drift: smoothstep ramp toward the variant target.
      baseTarget =
        this.driftTargetX * smoothstep01((ctx.t - ctx.cueT) / DRIFT_RAMP_S);
    } else {
      baseTarget = 0;
    }
    const sway =
      this.mirrorSign *
      SWAY_AMPLITUDE *
      Math.sin(2 * Math.PI * SWAY_HZ * ctx.t + this.swayPhase);
    const target = baseTarget + sway;

    // --- Longitudinal speed. ---
    let speed = PED_WALK_SPEED;
    if (standing) {
      speed = 0;
    } else if (ctx.reactive && !passed) {
      const rvx = ctx.robot.speed * Math.sin(ctx.robot.heading) - s.vx;
      const rvz = ctx.robot.speed * Math.cos(ctx.robot.heading) - s.vz;
      const ttc = timeToCollision(
        ctx.robot.x - s.x,
        ctx.robot.z - s.z,
        rvx,
        rvz,
        ctx.robotShape.effR + PED_RADIUS + SAFETY_MARGIN,
      );
      if (ttc < PED_REACTIVE_TTC) speed = PED_REACTIVE_SLOW_SPEED;
    }

    // --- Integrate (rate-limited lateral, constant longitudinal). ---
    const prevBaseX = this.baseX;
    this.baseX += clamp(baseTarget - this.baseX, -LATERAL_SPEED * ctx.dt, LATERAL_SPEED * ctx.dt);
    s.x += clamp(target - s.x, -LATERAL_SPEED * ctx.dt, LATERAL_SPEED * ctx.dt);
    s.z -= speed * ctx.dt;
    s.vx = (s.x - prevX) / ctx.dt;
    s.vz = (s.z - prevZ) / ctx.dt;
    s.safetyVx = (this.baseX - prevBaseX) / ctx.dt;
    s.safetyVz = s.vz;

    if (s.z < -2) {
      s.active = false;
      s.vx = 0;
      s.vz = 0;
      s.safetyVx = 0;
      s.safetyVz = 0;
    }
  }
}

/**
 * Calibration level 3: a pedestrian crossing the alley laterally. Starts
 * standing near one wall; begins crossing once the robot approaches; stands
 * at the far side afterwards. Teaches "release the joystick to yield".
 */
export class CrossingPedestrian implements PedestrianModel {
  readonly state: PedestrianState;
  private readonly fromX: number;
  private readonly toX: number;
  private readonly triggerZ: number;
  private started = false;
  private done = false;

  constructor(mirrorSign: 1 | -1, crossZ: number, crossX: number, triggerZ: number) {
    this.fromX = -crossX * mirrorSign;
    this.toX = crossX * mirrorSign;
    this.triggerZ = triggerZ;
    this.state = {
      x: this.fromX,
      z: crossZ,
      vx: 0,
      vz: 0,
      safetyVx: 0,
      safetyVz: 0,
      active: true,
    };
  }

  driftSide(): Side | null {
    return null;
  }

  step(ctx: PedStepCtx): void {
    const s = this.state;
    if (!s.active || this.done) {
      s.vx = 0;
      s.vz = 0;
      s.safetyVx = 0;
      s.safetyVz = 0;
      return;
    }
    if (!this.started && ctx.robot.z > this.triggerZ) this.started = true;
    if (!this.started) return;

    const dir = Math.sign(this.toX - this.fromX);
    const speed = 0.8;
    const prevX = s.x;
    s.x += dir * speed * ctx.dt;
    if ((dir > 0 && s.x >= this.toX) || (dir < 0 && s.x <= this.toX)) {
      s.x = this.toX;
      this.done = true;
    }
    s.vx = (s.x - prevX) / ctx.dt;
    s.vz = 0;
    // No gait sway on the crosser: safety velocity = actual velocity.
    s.safetyVx = s.vx;
    s.safetyVz = 0;
  }
}
